import "./Analytics.css";
import api from "../api.js";
import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
    ResponsiveContainer,
    BarChart, Bar,
    LineChart, Line,
    PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { fmtInt, fmtPercent, localISODate } from '../utils/format';

// Палитра статусов — читается одинаково в светлой и тёмной теме.
// Палитра графиков из дизайн-системы: зелёный логотипа — успех, бирюзовый — отправлено,
// тёмно-зелёный — прочитано, янтарный — в очереди, красный — ошибки
const COLORS = {
    sent: "#0B7F7B",
    delivered: "#31C702",
    failed: "#E5484D",
    enqueued: "#F5A623",
    read: "#1D7D02",
    cost: "#239302",
};

// «2026-10-31» → «31.10» на оси дат
const shortDate = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? `${v.slice(8, 10)}.${v.slice(5, 7)}` : v);

const STATUS_LABELS = {
    sent: "Отправлено",
    delivered: "Доставлено",
    failed: "Не отправлено",
    enqueued: "В очереди",
    read: "Прочитано",
};

const toISO = (d) => localISODate(d);

const formatMoney = (v) =>
    Number(v || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Стоимость (KPI, «Стоимость по дням», столбцы стоимости по категориям) пока скрыта у всех (решение 28.10.2026).
const SHOW_COST = false;

export default function Analytics() {
    const { me } = useOutletContext() || {};
    const role = me?.userInfo?.role;
    const myPartnerName = me?.userInfo?.partnerName;

    const [partners, setPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [stats, setStats] = useState([]);      // /api/messages/stats
    const [messages, setMessages] = useState([]); // /api/messages/allMessages

    const today = new Date();
    const monthAgo = new Date();
    monthAgo.setDate(today.getDate() - 29);

    const [fromDate, setFromDate] = useState(toISO(monthAgo));
    const [toDate, setToDate] = useState(toISO(today));

    const fetchPartners = async () => {
        try {
            const res = await api.get("/api/partners/list");
            setPartners(res.data || []);
        } catch {
            /* не критично для дашборда */
        }
    };

    const fetchData = async () => {
        if (!role) return;
        if (role === "Партнер" && !myPartnerName) {
            setError("Ваша учетная запись не привязана к партнеру");
            return;
        }
        setLoading(true);
        setError("");

        const params = { from: fromDate, to: toDate };
        if (role === "Партнер") params.partnerName = myPartnerName;
        else if (selectedPartner) params.partnerName = selectedPartner;

        try {
            const [statsRes, msgRes] = await Promise.all([
                api.get("/api/messages/stats", { params }),
                api.get("/api/messages/allMessages", { params }),
            ]);
            setStats(statsRes.data || []);
            setMessages(msgRes.data || []);
        } catch (err) {
            setError(err.response?.data || "Ошибка загрузки аналитики");
            setStats([]);
            setMessages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = "Аналитика - WAve";
        if (role === "Админ") fetchPartners();
    }, [role]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role, selectedPartner, fromDate, toDate]);

    // Объём по дням: суммируем строки stats (у админа их несколько на дату — по партнёрам).
    const volumeByDate = useMemo(() => {
        const map = new Map();
        for (const s of stats) {
            const row = map.get(s.date) || { date: s.date, sent: 0, delivered: 0, error: 0 };
            row.sent += s.sent || 0;
            row.delivered += s.delivered || 0;
            row.error += s.error || 0;
            map.set(s.date, row);
        }
        return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
    }, [stats]);

    // Стоимость по дням и статусы/категории — из детальных сообщений.
    const { costByDate, statusCounts, byCategory, totals } = useMemo(() => {
        const costMap = new Map();
        const status = { sent: 0, delivered: 0, failed: 0, enqueued: 0, read: 0 };
        const cat = new Map();
        let totalCost = 0;

        for (const m of messages) {
            const day = (m.date || "").split("T")[0];
            const price = m.price == null ? 0 : Number(m.price);
            if (price) {
                costMap.set(day, (costMap.get(day) || 0) + price);
                totalCost += price;
            }
            if (status[m.status] !== undefined) status[m.status]++;

            const c = m.category || "—";
            const entry = cat.get(c) || { category: c, count: 0, cost: 0 };
            entry.count++;
            entry.cost += price;
            cat.set(c, entry);
        }

        return {
            costByDate: Array.from(costMap.entries())
                .map(([date, cost]) => ({ date, cost: Number(cost.toFixed(4)) }))
                .sort((a, b) => a.date.localeCompare(b.date)),
            statusCounts: status,
            byCategory: Array.from(cat.values()).sort((a, b) => b.count - a.count),
            totals: { totalCost, totalMessages: messages.length },
        };
    }, [messages]);

    const kpis = useMemo(() => {
        const sent = volumeByDate.reduce((a, r) => a + r.sent, 0);
        const delivered = volumeByDate.reduce((a, r) => a + r.delivered, 0);
        const errors = volumeByDate.reduce((a, r) => a + r.error, 0);
        const deliveryRate = sent > 0 ? (delivered / sent) * 100 : 0;
        return { sent, delivered, errors, deliveryRate, cost: totals.totalCost };
    }, [volumeByDate, totals]);

    const statusPie = useMemo(
        () =>
            Object.entries(statusCounts)
                .filter(([, v]) => v > 0)
                .map(([k, v]) => ({ name: STATUS_LABELS[k] || k, key: k, value: v })),
        [statusCounts]
    );

    const hasData = volumeByDate.length > 0 || messages.length > 0;

    return (
        <div className="analytics-main">
            <div className="analytics-wrapper">
                <form className="analytics-filters" onSubmit={(e) => { e.preventDefault(); fetchData(); }}>
                    {role === "Админ" && (
                        <label>
                            Партнер:
                            <select value={selectedPartner} onChange={(e) => setSelectedPartner(e.target.value)}>
                                <option value="">Все</option>
                                {partners.map((p) => (
                                    <option key={p.id} value={p.name}>{p.name}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label>
                        С:
                        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                    </label>
                    <label>
                        По:
                        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                    </label>
                </form>

                {error && <p className="error">{typeof error === "string" ? error : "Ошибка"}</p>}

                <div className="kpi-grid">
                    <div className="kpi-card"><p>Отправлено</p><b>{fmtInt(kpis.sent)}</b></div>
                    <div className="kpi-card"><p>Доставлено</p><b>{fmtInt(kpis.delivered)}</b></div>
                    <div className="kpi-card"><p>Доставляемость</p><b>{fmtPercent(kpis.deliveryRate)}</b></div>
                    <div className="kpi-card"><p>Ошибки</p><b>{fmtInt(kpis.errors)}</b></div>
                    {SHOW_COST && <div className="kpi-card kpi-cost"><p>Стоимость</p><b>{formatMoney(kpis.cost)}</b></div>}
                </div>

                {loading ? (
                    <p className="analytics-empty">Загрузка...</p>
                ) : !hasData ? (
                    <p className="analytics-empty">{error ? "" : "Нет данных за выбранный период"}</p>
                ) : (
                    <div className="charts-grid">
                        <div className="chart-card chart-wide">
                            <h3>Объём по дням</h3>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={volumeByDate}>
                                    <CartesianGrid strokeDasharray="3 3" className="chart-grid" />
                                    <XAxis dataKey="date" tickFormatter={shortDate} />
                                    <YAxis allowDecimals={false} tickFormatter={fmtInt} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="sent" name="Отправлено" stackId="a" fill={COLORS.sent} />
                                    <Bar dataKey="delivered" name="Доставлено" stackId="a" fill={COLORS.delivered} />
                                    <Bar dataKey="error" name="Ошибки" stackId="a" fill={COLORS.failed} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className={"chart-card" + (SHOW_COST ? "" : " chart-wide")}>
                            <h3>Распределение по статусам</h3>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={statusPie} dataKey="value" nameKey="name" outerRadius={95} label>
                                        {statusPie.map((s) => (
                                            <Cell key={s.key} fill={COLORS[s.key] || "#9CA3AF"} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        {SHOW_COST && (
                            <div className="chart-card">
                                <h3>Стоимость по дням</h3>
                                <ResponsiveContainer width="100%" height={280}>
                                    <LineChart data={costByDate}>
                                        <CartesianGrid strokeDasharray="3 3" className="chart-grid" />
                                        <XAxis dataKey="date" tickFormatter={shortDate} />
                                        <YAxis />
                                        <Tooltip formatter={(v) => formatMoney(v)} />
                                        <Line type="monotone" dataKey="cost" name="Стоимость" stroke={COLORS.cost} strokeWidth={2} dot={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}

                        {role === "Админ" && (
                            <div className="chart-card chart-wide">
                                <h3>По категориям</h3>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={byCategory}>
                                        <CartesianGrid strokeDasharray="3 3" className="chart-grid" />
                                        <XAxis dataKey="category" />
                                        <YAxis yAxisId="left" allowDecimals={false} tickFormatter={fmtInt} />
                                        {SHOW_COST && <YAxis yAxisId="right" orientation="right" />}
                                        <Tooltip formatter={(v, n) => (n === "Стоимость" ? formatMoney(v) : v)} />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="count" name="Сообщений" fill={COLORS.sent} />
                                        {SHOW_COST && <Bar yAxisId="right" dataKey="cost" name="Стоимость" fill={COLORS.cost} />}
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
